/**
 * Admin — add amenity: name, hours, slot length, rules, paid toggle + fee.
 * Wired: POST /api/admin/amenities. Port of the create-vendor.tsx pattern
 * (SimpleCreate), extended with the amenity-specific fields.
 */
import React from 'react';
import { View } from 'react-native';

import { formatINR, space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { AdminScreenShell, goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { Button, Chip, Field, IconButton, Input, Screen } from '@/ui';

export default function CreateAmenityScreen() {
  const { colors } = useTheme();
  const [name, setName] = React.useState('');
  const [openHour, setOpenHour] = React.useState('6');
  const [closeHour, setCloseHour] = React.useState('22');
  const [slotMinutes, setSlotMinutes] = React.useState('120');
  const [rules, setRules] = React.useState('');
  const [isPaid, setIsPaid] = React.useState(false);
  const [feePaise, setFeePaise] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const canSave = name.trim().length > 0
    && Number.isFinite(Number(openHour)) && Number.isFinite(Number(closeHour))
    && Number.isFinite(Number(slotMinutes));

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/admin/amenities', {
        name: name.trim(),
        openHour: Number(openHour),
        closeHour: Number(closeHour),
        slotMinutes: Number(slotMinutes),
        rules: rules.trim(),
        isPaid,
        feePaise: isPaid ? Math.max(0, Math.round((Number(feePaise) || 0) * 100)) : 0,
      });
      goBack();
    } catch {
      setNotice('Could not add the amenity right now — check the fields and try again.');
      setSaving(false);
    }
  };

  return (
    <AdminScreenShell
      title="Add amenity"
      right={
        <IconButton label="Discard" onPress={goBack}>
          <Icon name="x" size={20} color={colors.textPrimary} />
        </IconButton>
      }
    >
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label="Name">
          <Input value={name} onChangeText={setName} placeholder="Gym, Swimming Pool, Yoga Deck…" />
        </Field>
        <View style={{ flexDirection: 'row', gap: space.s3 }}>
          <View style={{ flex: 1 }}>
            <Field label="Opens (24h)">
              <Input value={openHour} onChangeText={setOpenHour} keyboardType="number-pad" placeholder="6" />
            </Field>
          </View>
          <View style={{ flex: 1 }}>
            <Field label="Closes (24h)">
              <Input value={closeHour} onChangeText={setCloseHour} keyboardType="number-pad" placeholder="22" />
            </Field>
          </View>
        </View>
        <Field label="Slot length (minutes)">
          <Input value={slotMinutes} onChangeText={setSlotMinutes} keyboardType="number-pad" placeholder="120" />
        </Field>
        <Field label="Rules" help="One rule per line — shown to residents under the slot grid.">
          <Input
            value={rules}
            onChangeText={setRules}
            multiline
            style={{ minHeight: 96, textAlignVertical: 'top' }}
            placeholder={'Maximum 1 booking per day per flat\nCancel at least 2 hours before your slot'}
          />
        </Field>
        <Field label="Charges">
          <View style={{ flexDirection: 'row', gap: space.s2 }}>
            <Chip label="Free" active={!isPaid} onPress={() => setIsPaid(false)} />
            <Chip label="Paid" active={isPaid} onPress={() => setIsPaid(true)} />
          </View>
        </Field>
        {isPaid ? (
          <Field
            label="Fee per slot (₹)"
            help={Number(feePaise) > 0 ? `Residents see ${formatINR(Number(feePaise) || 0)} per slot` : 'e.g. 200'}
          >
            <Input value={feePaise} onChangeText={setFeePaise} keyboardType="number-pad" placeholder="200" />
          </Field>
        ) : null}
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
      </Screen>
      <BottomBar>
        <Button title="Add amenity" size="lg" disabled={!canSave} loading={saving} onPress={() => { void save(); }} />
      </BottomBar>
    </AdminScreenShell>
  );
}
